import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import getAsyncStatus, {
  AsyncOperationStatus,
} from "../http/getAsyncStatus";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

const POLL_INTERVAL_MS = 3000;
const TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

interface UseAsyncOperationParams {
  siteId: string;
  operationKey: string | null;
  enabled?: boolean;
}

export const useAsyncOperation = ({
  siteId,
  operationKey,
  enabled = true,
}: UseAsyncOperationParams) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  const [timedOut, setTimedOut] = useState(false);
  const startTimeRef = useRef<number | null>(null);

  const isActive = enabled && !!operationKey && !timedOut;

  // Track start time
  useEffect(() => {
    if (isActive && startTimeRef.current === null) {
      startTimeRef.current = Date.now();
    }
    if (!isActive) {
      startTimeRef.current = null;
      setTimedOut(false);
    }
  }, [isActive]);

  const query = useQuery<AsyncOperationStatus>(
    ["async_status", siteId, operationKey],
    async () => {
      // Check client-side timeout
      if (startTimeRef.current && Date.now() - startTimeRef.current > TIMEOUT_MS) {
        setTimedOut(true);
        throw new Error("Operation timed out after 15 minutes");
      }
      return getAsyncStatus(
        await getAuthTokenBlocking(getToken, relogin),
        siteId,
        operationKey!
      );
    },
    {
      enabled: isActive,
      refetchInterval: (data) => {
        if (!data) return POLL_INTERVAL_MS;
        if (data.status === "pending" || data.status === "running") {
          return POLL_INTERVAL_MS;
        }
        return false; // Stop polling when completed or failed
      },
      staleTime: 0,
      cacheTime: 0,
    }
  );

  return {
    ...query,
    timedOut,
    isPolling:
      isActive &&
      query.data?.status !== "completed" &&
      query.data?.status !== "failed",
  };
};
